/**
 * The turn loop — the engine's single public entry point. [P1-place] [P1-meeple] [P1-score-end]
 *
 * `applyAction(state, action, tiles)` is pure, deterministic and total:
 *   - it never mutates `state`,
 *   - it never throws on bad input; an illegal action is a `RuleViolation` value,
 *   - and given the same inputs it always produces the same outputs.
 *
 * The server calls it to commit moves. The client calls it (and the `legal*` helpers) to predict what
 * is allowed, so it can highlight placements without a round trip. Same code both sides, so they
 * cannot drift apart on the rules.
 *
 * A turn is: draw → place the tile → optionally place a meeple → score what finished → next seat.
 */
import { createForest, edgeAt, find, integrateTile, nodeId } from './features.js';
import { occupantsOf, scoreCompletions, scoreEndgameFeatures, scoreFarms, } from './scoring.js';
import { createRng, shuffle } from './rng.js';
import { buildDeck, buildTileIndex, START_TILE_ID } from './tiles/index.js';
import { BASE_SUPPLY, OPPOSITE, SIDES, SIDE_OFFSET, err, ok, posFromKey, posKey, supplyOf, totalSupply } from './types.js';
export const ROTATIONS = [0, 1, 2, 3];
// ── Setup ─────────────────────────────────────────────────────────────────────────────────────────
export function createGame(options) {
    const { seed, seatCount } = options;
    const packs = options.packs ?? ['base'];
    const stackCount = Math.max(1, Math.min(6, options.stackCount ?? 1));
    const tiles = buildTileIndex(packs);
    const shuffled = shuffle(createRng(seed), buildDeck(packs));
    const stacks = Array.from({ length: stackCount }, () => []);
    shuffled.value.forEach((defId, i) => {
        stacks[i % stackCount].push(defId);
    });
    const start = { defId: START_TILE_ID, rotation: 0, pos: { x: 0, y: 0 }, drawIndex: 0 };
    const board = new Map([[posKey(start.pos), start]]);
    const forest = integrateTile(createForest(), board, tiles, start);
    return {
        seed,
        rng: shuffled.rng,
        seatCount,
        phase: 'awaiting_draw',
        activeSeat: 0,
        board,
        stacks,
        currentTile: null,
        lastPlaced: null,
        meeples: [],
        supply: Array.from({ length: seatCount }, () => ({ ...BASE_SUPPLY })),
        scores: Array.from({ length: seatCount }, () => 0),
        forest,
        discarded: [],
        drawIndexCounter: 1,
    };
}
// ── Legality helpers (also used by the client for highlighting, and by the AI) ────────────────────
/** Can this tile, at this rotation, legally occupy this cell? */
export function canPlace(state, tiles, defId, pos, rotation) {
    if (state.board.has(posKey(pos)))
        return false;
    const def = tiles.get(defId);
    if (!def)
        return false;
    let touchesSomething = false;
    for (const side of SIDES) {
        const offset = SIDE_OFFSET[side];
        const neighbour = state.board.get(posKey({ x: pos.x + offset.x, y: pos.y + offset.y }));
        if (!neighbour)
            continue;
        const neighbourDef = tiles.get(neighbour.defId);
        if (!neighbourDef)
            continue;
        touchesSomething = true;
        if (edgeAt(def, rotation, side) !== edgeAt(neighbourDef, neighbour.rotation, OPPOSITE[side])) {
            return false;
        }
    }
    // A tile may never be dropped into open space — the board is one connected region.
    return touchesSomething;
}
/**
 * Which sides stop this tile going here — empty if it fits.
 *
 * Exists because "that tile doesn't fit there" is a true statement that teaches nothing. A cell can
 * have up to FOUR neighbours and every one of them binds; a player who has checked the two sides they
 * can see, and found both matching, will read the refusal as a bug rather than as a rule. Naming the
 * offending side turns an argument into an observation.
 *
 * Returns `[]` for a cell that is occupied or floating in space — those are refusals for a different
 * reason and the caller already knows which.
 */
export function placementConflicts(state, tiles, defId, pos, rotation) {
    const def = tiles.get(defId);
    if (!def || state.board.has(posKey(pos)))
        return [];
    const out = [];
    for (const side of SIDES) {
        const offset = SIDE_OFFSET[side];
        const neighbour = state.board.get(posKey({ x: pos.x + offset.x, y: pos.y + offset.y }));
        if (!neighbour)
            continue;
        const neighbourDef = tiles.get(neighbour.defId);
        if (!neighbourDef)
            continue;
        const mine = edgeAt(def, rotation, side);
        const theirs = edgeAt(neighbourDef, neighbour.rotation, OPPOSITE[side]);
        if (mine !== theirs)
            out.push({ side, mine, theirs });
    }
    return out;
}
/** Every legal (position, rotation) for a tile. The empty array means the tile must be discarded. */
export function legalPlacements(state, tiles, defId) {
    const candidates = new Set();
    for (const key of state.board.keys()) {
        const pos = posFromKey(key);
        for (const side of SIDES) {
            const offset = SIDE_OFFSET[side];
            const candidate = { x: pos.x + offset.x, y: pos.y + offset.y };
            const candidateKey = posKey(candidate);
            if (!state.board.has(candidateKey))
                candidates.add(candidateKey);
        }
    }
    const out = [];
    for (const key of candidates) {
        const pos = posFromKey(key);
        for (const rotation of ROTATIONS) {
            if (canPlace(state, tiles, defId, pos, rotation))
                out.push({ pos, rotation });
        }
    }
    return out;
}
/**
 * Which features of the just-placed tile can take a meeple.
 *
 * The subtlety: occupancy is judged AFTER the tile has been integrated into the forest. If the tile
 * just joined your empty road to someone else's occupied road, they are now one road, and it is no
 * longer free.
 */
export function legalMeepleTargets(state, tiles) {
    if (state.phase !== 'awaiting_meeple' || !state.lastPlaced)
        return [];
    if (totalSupply(state.supply[state.activeSeat]) <= 0)
        return [];
    const placed = state.board.get(posKey(state.lastPlaced));
    if (!placed)
        return [];
    const def = tiles.get(placed.defId);
    if (!def)
        return [];
    const out = [];
    for (let i = 0; i < def.features.length; i++) {
        const root = find(state.forest, nodeId(state.lastPlaced, i));
        if (occupantsOf(state.forest, state.meeples, root).length === 0)
            out.push(i);
    }
    return out;
}
// ── The reducer ───────────────────────────────────────────────────────────────────────────────────
export function applyAction(state, action, tiles = buildTileIndex()) {
    if (state.phase === 'game_over') {
        return err('game_over', 'The game is over.');
    }
    if (action.seat !== state.activeSeat) {
        return err('not_your_turn', `It is seat ${state.activeSeat}'s turn, not seat ${action.seat}'s.`);
    }
    switch (action.type) {
        case 'draw':
            return applyDraw(state, tiles, action.stack);
        case 'place_tile':
            return applyPlaceTile(state, tiles, action.pos, action.rotation);
        case 'place_meeple':
            return applyPlaceMeeple(state, tiles, action.featureIndex, action.kind ?? 'follower');
        case 'skip_meeple':
            return applySkipMeeple(state, tiles);
    }
}
function applyDraw(state, tiles, stack) {
    if (state.phase !== 'awaiting_draw') {
        return err('wrong_phase', `Cannot draw while ${state.phase}.`);
    }
    const nonEmpty = state.stacks
        .map((s, i) => ({ s, i }))
        .filter(({ s }) => s.length > 0);
    if (nonEmpty.length === 0) {
        return err('deck_empty', 'There are no tiles left to draw.');
    }
    let chosen = stack ?? nonEmpty[0].i;
    if (stack !== undefined) {
        if (!Number.isInteger(stack) || stack < 0 || stack >= state.stacks.length) {
            return err('invalid_stack', `Stack ${stack} does not exist.`);
        }
        if ((state.stacks[stack] ?? []).length === 0) {
            return err('invalid_stack', `Stack ${stack} is empty.`);
        }
        chosen = stack;
    }
    // Draw, and keep drawing past any tile that cannot legally be placed anywhere on the board. The
    // official rule is to discard such a tile and take another; with a 72-tile deck it is rare but real.
    const stacks = state.stacks.map((s) => s.slice());
    const discarded = [...state.discarded];
    const events = [];
    let drawIndex = state.drawIndexCounter;
    let working = state;
    for (;;) {
        let source = stacks[chosen];
        if (!source || source.length === 0) {
            const next = stacks.findIndex((s) => s.length > 0);
            if (next < 0) {
                // Everything left was unplaceable — the game ends here.
                return finishGame({ ...state, stacks, discarded, drawIndexCounter: drawIndex }, tiles, events);
            }
            chosen = next;
            source = stacks[chosen];
        }
        const defId = source.pop();
        working = { ...state, stacks, discarded, drawIndexCounter: drawIndex };
        if (legalPlacements(working, tiles, defId).length > 0) {
            events.push({ type: 'tile_drawn', seat: state.activeSeat, defId, drawIndex });
            return ok({
                state: {
                    ...working,
                    phase: 'awaiting_placement',
                    currentTile: { defId, drawIndex },
                    drawIndexCounter: drawIndex + 1,
                },
                events,
            });
        }
        discarded.push(defId);
        events.push({ type: 'tile_discarded', seat: state.activeSeat, defId, reason: 'unplaceable' });
        drawIndex += 1;
    }
}
function applyPlaceTile(state, tiles, pos, rotation) {
    if (state.phase !== 'awaiting_placement' || !state.currentTile) {
        return err('no_tile_drawn', 'No tile has been drawn to place.');
    }
    if (state.board.has(posKey(pos))) {
        return err('cell_occupied', `There is already a tile at ${posKey(pos)}.`);
    }
    const def = tiles.get(state.currentTile.defId);
    if (!def)
        return err('no_tile_drawn', `Unknown tile ${state.currentTile.defId}.`);
    // Distinguish the two illegal-placement reasons so the client can say something useful.
    let touches = false;
    for (const side of SIDES) {
        const offset = SIDE_OFFSET[side];
        const neighbour = state.board.get(posKey({ x: pos.x + offset.x, y: pos.y + offset.y }));
        if (!neighbour)
            continue;
        touches = true;
        const neighbourDef = tiles.get(neighbour.defId);
        if (!neighbourDef)
            continue;
        if (edgeAt(def, rotation, side) !== edgeAt(neighbourDef, neighbour.rotation, OPPOSITE[side])) {
            return err('edges_do_not_match', 'That tile does not fit — the edges disagree.');
        }
    }
    if (!touches) {
        return err('no_adjacent_tile', 'A tile must be placed next to one already on the board.');
    }
    const placed = {
        defId: state.currentTile.defId,
        rotation,
        pos,
        drawIndex: state.currentTile.drawIndex,
    };
    const board = new Map(state.board);
    board.set(posKey(pos), placed);
    const forest = integrateTile(state.forest, board, tiles, placed);
    return ok({
        state: {
            ...state,
            board,
            forest,
            phase: 'awaiting_meeple',
            lastPlaced: pos,
            currentTile: null,
        },
        events: [
            { type: 'tile_placed', seat: state.activeSeat, defId: placed.defId, pos, rotation },
        ],
    });
}
function applyPlaceMeeple(state, tiles, featureIndex, kind) {
    if (state.phase !== 'awaiting_meeple' || !state.lastPlaced) {
        return err('wrong_phase', 'There is no freshly placed tile to put a meeple on.');
    }
    if (supplyOf(state.supply[state.activeSeat], kind) <= 0) {
        return err('no_meeples_left', kind === 'follower'
            ? 'You have no meeples left in your supply.'
            : `You have no ${kind} left in your supply.`);
    }
    const placed = state.board.get(posKey(state.lastPlaced));
    if (!placed)
        return err('wrong_phase', 'The last placed tile has gone missing.');
    const def = tiles.get(placed.defId);
    if (!def || !def.features[featureIndex]) {
        return err('invalid_feature', `That tile has no feature ${featureIndex}.`);
    }
    const root = find(state.forest, nodeId(state.lastPlaced, featureIndex));
    if (occupantsOf(state.forest, state.meeples, root).length > 0) {
        return err('feature_occupied', 'Somebody is already on that feature — placing this tile joined it to theirs.');
    }
    const meeple = { seat: state.activeSeat, pos: state.lastPlaced, featureIndex, kind };
    const supply = state.supply.slice();
    supply[state.activeSeat] = {
        ...supply[state.activeSeat],
        [kind]: supplyOf(supply[state.activeSeat], kind) - 1,
    };
    const withMeeple = {
        ...state,
        meeples: [...state.meeples, meeple],
        supply,
    };
    return endTurn(withMeeple, tiles, [{ type: 'meeple_placed', meeple }]);
}
function applySkipMeeple(state, tiles) {
    if (state.phase !== 'awaiting_meeple') {
        return err('wrong_phase', 'There is nothing to skip right now.');
    }
    return endTurn(state, tiles, [{ type: 'meeple_skipped', seat: state.activeSeat }]);
}
// ── End of turn ───────────────────────────────────────────────────────────────────────────────────
/**
 * Which features might have just completed.
 *
 * The placed tile's own features, obviously — but also any cloister within one step in any direction,
 * because filling the last gap around someone else's cloister completes it even though the tile you
 * placed has nothing to do with cloisters.
 */
function completionCandidates(state, tiles, at) {
    const out = [];
    const placed = state.board.get(posKey(at));
    if (placed) {
        const def = tiles.get(placed.defId);
        if (def)
            for (let i = 0; i < def.features.length; i++)
                out.push(nodeId(at, i));
    }
    for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
            if (dx === 0 && dy === 0)
                continue;
            const pos = { x: at.x + dx, y: at.y + dy };
            const neighbour = state.board.get(posKey(pos));
            if (!neighbour)
                continue;
            const def = tiles.get(neighbour.defId);
            if (!def)
                continue;
            for (let i = 0; i < def.features.length; i++) {
                if (def.features[i]?.kind === 'cloister')
                    out.push(nodeId(pos, i));
            }
        }
    }
    return out;
}
function endTurn(state, tiles, events) {
    const at = state.lastPlaced;
    const candidates = at ? completionCandidates(state, tiles, at) : [];
    const completions = scoreCompletions(state.forest, state.board, tiles, state.meeples, candidates);
    const scores = state.scores.slice();
    const supply = state.supply.slice();
    let meeples = state.meeples;
    const out = [...events];
    for (const scored of completions) {
        if (scored.awards.length > 0) {
            out.push(scoreEvent(state, scored, 'during_play'));
            for (const award of scored.awards) {
                scores[award.seat] = (scores[award.seat] ?? 0) + award.points;
            }
        }
        if (scored.returned.length > 0) {
            const returning = new Set(scored.returned);
            meeples = meeples.filter((m) => !returning.has(m));
            // A scored meeple goes home to the pool it came from, not to a generic count.
            for (const m of scored.returned) {
                supply[m.seat] = { ...supply[m.seat], [m.kind]: supplyOf(supply[m.seat], m.kind) + 1 };
            }
            out.push({ type: 'meeples_returned', meeples: scored.returned });
        }
    }
    const afterScoring = { ...state, scores, supply, meeples, lastPlaced: null };
    const tilesLeft = afterScoring.stacks.some((s) => s.length > 0);
    if (!tilesLeft)
        return finishGame(afterScoring, tiles, out);
    const nextSeat = (state.activeSeat + 1) % state.seatCount;
    out.push({ type: 'turn_changed', seat: nextSeat });
    return ok({
        state: { ...afterScoring, phase: 'awaiting_draw', activeSeat: nextSeat },
        events: out,
    });
}
/** Final scoring: everything unfinished, then the farms, then the result. */
function finishGame(state, tiles, events) {
    const scores = state.scores.slice();
    const out = [...events];
    for (const scored of scoreEndgameFeatures(state.forest, state.board, tiles, state.meeples)) {
        if (scored.points === 0)
            continue;
        out.push(scoreEvent(state, scored, 'endgame'));
        for (const award of scored.awards)
            scores[award.seat] = (scores[award.seat] ?? 0) + award.points;
    }
    for (const scored of scoreFarms(state.forest, state.board, tiles, state.meeples)) {
        out.push(scoreEvent(state, scored, 'farms'));
        for (const award of scored.awards)
            scores[award.seat] = (scores[award.seat] ?? 0) + award.points;
    }
    const best = Math.max(...scores);
    const winners = scores.map((s, i) => ({ s, i })).filter(({ s }) => s === best).map(({ i }) => i);
    out.push({ type: 'game_ended', finalScores: scores, winners });
    return ok({
        state: { ...state, scores, phase: 'game_over', currentTile: null, lastPlaced: null },
        events: out,
    });
}
function scoreEvent(state, scored, phase) {
    return {
        type: 'feature_scored',
        kind: scored.kind,
        completed: phase === 'during_play',
        tiles: featureTilePositions(state.forest, scored.root, state.board),
        points: scored.points,
        seats: scored.awards.map((a) => a.seat),
        phase,
    };
}
function featureTilePositions(forest, root, board) {
    const seen = new Set();
    for (const node of forest.parent.keys()) {
        if (find(forest, node) !== root)
            continue;
        const hash = node.indexOf('#');
        const key = node.slice(0, hash);
        if (board.has(key))
            seen.add(key);
    }
    return [...seen].map(posFromKey);
}
/** Convenience for tests and the AI: run a list of actions and return the final state. */
export function applyAll(state, actions, tiles = buildTileIndex()) {
    let current = state;
    const events = [];
    for (const action of actions) {
        const result = applyAction(current, action, tiles);
        if (!result.ok)
            return result;
        current = result.value.state;
        events.push(...result.value.events);
    }
    return ok({ state: current, events });
}
//# sourceMappingURL=game.js.map